import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../../design-system";
import "./UnsyncDataModal.css";

// Inline SVG components for offline support
const CloudOffIcon = ({ width = 24, height = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.58 5.548c.24 -.11 .492 -.207 .752 -.286c1.88 -.572 3.956 -.193 5.444 1c1.488 1.19 2.162 3.007 1.77 4.769h.99c1.913 0 3.464 1.56 3.464 3.486c0 .957 -.383 1.824 -1.003 2.454m-2.997 1.03h-11.343c-2.572 -.004 -4.657 -2.011 -4.657 -4.487c0 -2.475 2.085 -4.482 4.657 -4.482c.393 -.003 .786 .043 1.167 .136" />
    <path d="M3 3l18 18" />
  </svg>
);

const CloseIcon = ({ width = 24, height = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const UnsyncDataModal = ({ isOpen, onClose, leadName }) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const [isIOSKeyboardOpen, setIsIOSKeyboardOpen] = useState(false);
  const { theme } = useTheme();

  // Pull-down gesture state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalRef = useRef(null);

  // Track window resize for responsive layout
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Track iOS keyboard state for backdrop-filter fix
  useEffect(() => {
    const checkIOSKeyboard = () => {
      const isOpen = document.documentElement.getAttribute('data-ios-keyboard') === 'open';
      setIsIOSKeyboardOpen(isOpen);
    };

    const observer = new MutationObserver(checkIOSKeyboard);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-ios-keyboard']
    });

    checkIOSKeyboard();
    return () => observer.disconnect();
  }, []);

  // Pull-down gesture handlers - MUST be defined before any early return
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    const modalRect = modalRef.current?.getBoundingClientRect();
    if (modalRect && touch.clientY - modalRect.top < 60) {
      dragStartY.current = touch.clientY;
      setIsDragging(true);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY.current;
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > 100) {
      onClose();
    } else {
      setDragY(0);
    }
  }, [isDragging, dragY, onClose]);

  // Early return AFTER all hooks are defined
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isLight = theme === "light";

  // Inline styles to match ScanLeadModal pattern
  const backdropStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isLight ? "rgba(0, 0, 0, 0.5)" : "rgba(255, 255, 255, 0.14)",
    // Disable backdrop-filter on iOS when keyboard is open to prevent gray overlay
    backdropFilter: isIOSKeyboardOpen ? "none" : "blur(5.5px)",
    WebkitBackdropFilter: isIOSKeyboardOpen ? "none" : "blur(5.5px)",
    zIndex: 2000,
  };

  const closeButtonStyle = {
    position: "fixed",
    bottom: isDesktop ? "calc(50% + 160px)" : "calc(280px + 16px)",
    left: "50%",
    transform: "translateX(-50%)",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: isLight ? "#FFFFFF" : "#262626",
    border: isLight ? "1px solid #E0E1E6" : "1px solid #404040",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: isLight ? "#0A0A0A" : "#FFFFFF",
    zIndex: 2002,
    padding: 0,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  };

  const modalStyle = {
    position: "fixed",
    ...(isDesktop ? {
      bottom: "50%",
      left: "50%",
      right: "auto",
      transform: "translate(-50%, 50%)",
      width: "100%",
      maxWidth: "390px",
      borderRadius: "20px",
      border: isLight ? "1px solid #E0E1E6" : "1px solid #3A3939",
    } : {
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: "20px 20px 0 0",
      borderTop: isLight ? "1px solid #E0E1E6" : "1px solid #3A3939",
    }),
    backgroundColor: isLight ? "#ffffff" : "#0A0A0A",
    boxShadow: isLight ? "0px 0px 10px rgba(0, 0, 0, 0.1)" : "0px 0px 10px rgba(255, 255, 255, 0.3)",
    zIndex: 2001,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "16px 20px 20px",
    gap: "20px",
  };

  const dragHandleStyle = {
    width: "32px",
    height: "6px",
    backgroundColor: isLight ? "rgba(0, 0, 0, 0.2)" : "rgba(255, 255, 255, 0.2)",
    borderRadius: "3px",
  };

  const contentStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: "20px 0",
  };

  const iconStyle = {
    width: "60px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
  };

  const nameStyle = {
    fontSize: "20px",
    fontWeight: 600,
    color: isLight ? "#0A0A0A" : "#ffffff",
    margin: 0,
    textAlign: "center",
    textTransform: "capitalize",
  };

  const messageStyle = {
    fontSize: "16px",
    fontWeight: 400,
    color: isLight ? "#535358" : "#A7A7B3",
    margin: 0,
    textAlign: "center",
    lineHeight: 1.5,
    maxWidth: "280px",
    backgroundColor: isLight ? "#F5F6FA" : "#1A1A1A",
    padding: "10px 12px",
    borderRadius: "8px",
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="unsync-modal__backdrop"
        style={backdropStyle}
        onClick={handleBackdropClick}
      />

      {/* Close Button (Above Modal) */}
      <button
        className="unsync-modal__close-btn"
        style={{
          ...closeButtonStyle,
          transform: dragY > 0
            ? `translateX(-50%) translateY(${dragY}px)`
            : closeButtonStyle.transform,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        }}
        onClick={onClose}
        aria-label="Close"
      >
        <CloseIcon width={24} height={24} />
      </button>

      {/* Modal */}
      <div
        className="unsync-modal"
        style={{
          ...modalStyle,
          transform: dragY > 0
            ? (isDesktop ? `translate(-50%, calc(50% + ${dragY}px))` : `translateY(${dragY}px)`)
            : modalStyle.transform,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        }}
        ref={modalRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        <div className="unsync-modal__drag-handle" style={{...dragHandleStyle, cursor: 'grab'}} />

        {/* Content */}
        <div className="unsync-modal__content" style={contentStyle}>
          {/* Cloud Off Icon */}
          <div className="unsync-modal__icon" style={iconStyle}>
            <CloudOffIcon width={60} height={60} />
          </div>

          {/* Lead Name */}
          <h3 className="unsync-modal__name" style={nameStyle}>{leadName || "Lead"}</h3>

          {/* Message */}
          <p className="unsync-modal__message" style={messageStyle}>
            The lead data is not synced and will sync once you are online
          </p>
        </div>
      </div>
    </>
  );
};

export default UnsyncDataModal;
